import { useEffect, useState } from 'react';
import { Gauge } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface StageVelocity {
  stageId: string | null;
  stageName: string | null;
  stageOrder: number | null;
  averageDaysInStage: number;
  sampleSize: number | null;
}

/**
 * Story 56.3 — pipeline velocity. Average days in each stage so a
 * manager can see where deals stall.
 */
export function PipelineVelocityWidget() {
  const [data, setData] = useState<StageVelocity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<StageVelocity[]>('/analytics/pipeline-velocity')
      .then(({ data }) => setData(data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading velocity…
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        No stage transitions recorded yet — velocity will populate once deals
        start moving.
      </div>
    );
  }

  const sorted = [...data].sort(
    (a, b) => (a.stageOrder ?? 99) - (b.stageOrder ?? 99),
  );
  const maxDays = Math.max(
    1,
    ...sorted.map((r) => Number(r.averageDaysInStage) || 0),
  );

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Gauge className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Pipeline velocity (avg days per stage)
        </h3>
      </div>
      <div className="space-y-2">
        {sorted.map((row) => {
          const days = Number(row.averageDaysInStage) || 0;
          const pct = (days / maxDays) * 100;
          const slow = days >= 14;
          return (
            <div key={row.stageId ?? row.stageName ?? Math.random()}>
              <div className="flex justify-between text-xs">
                <span className="font-medium">{row.stageName ?? '—'}</span>
                <span className={slow ? 'font-semibold text-rose-300' : 'text-muted-foreground'}>
                  {days.toFixed(1)}d{row.sampleSize ? ` · ${row.sampleSize}` : ''}
                </span>
              </div>
              <div className="mt-1 h-2 w-full rounded bg-muted">
                <div
                  className={`h-2 rounded transition-all ${
                    slow ? 'bg-rose-500/60' : 'bg-emerald-500/60'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
